import React, { createContext, useCallback, useContext, useState, useMemo } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

/**
 * Notification context: replace window.alert with non-blocking snackbars.
 * showMessage(text, { severity: 'info'|'success'|'warning'|'error', persist:boolean })
 */
const NotificationContext = createContext({ showMessage: () => {} });
export const useNotify = () => useContext(NotificationContext);

let idCounter = 0;

export const NotificationProvider = ({ children }) => {
  const [messages, setMessages] = useState([]); // { id, text, severity, persist }

  const showMessage = useCallback((text, opts = {}) => {
    const { severity = 'info', persist = false } = opts;
    const id = ++idCounter;
    setMessages(prev => [...prev, { id, text, severity, persist, open: true }]);
    return id;
  }, []);

  const handleClose = id => (event, reason) => {
    if (reason === 'clickaway') return;
    setMessages(prev => prev.map(m => (m.id === id ? { ...m, open: false } : m)));
    setTimeout(() => setMessages(prev => prev.filter(m => m.id !== id)), 300);
  };

  const contextValue = useMemo(() => ({ showMessage }), [showMessage]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {messages.map(m => (
        <Snackbar
          key={m.id}
          open={m.open}
          autoHideDuration={m.persist ? null : 4000}
          onClose={handleClose(m.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleClose(m.id)}
            severity={m.severity}
            variant='filled'
            sx={{ alignItems: 'flex-start', whiteSpace: 'pre-line', maxWidth: 420 }}
          >
            {m.text}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
